import { ChartSortEnum } from '../../../components/Chart/interfaces';
import { FunnelDefaultConversionRateSetting } from '../../../utils/apollo/graphqlSchema';
import { IChartSettings } from '../../../components/Chart/interfaces';
import { Maybe } from '../../../utils/apollo/graphqlSchema';
import { IReportSettingFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import { UnreachableCaseError } from 'ts-essentials';
import {
  defaultTableSettings,
  generateDisplayedSliceSettings,
  initialChartSettings,
} from './utils';
import { assertNotNil } from '../../../utils/types';
import { isTransientAnalysisId } from '../transientAnalysis';
import { IDisplayedMeasureSetting } from '../../../components/Chart/interfaces';
import { FunnelModeKey } from '../../../utils/apollo/graphqlSchema';
import { IFunnelAnalysis } from '../../../utils/apollo/responseTypes';
import { mapDefaultChartKindToChartType } from './utils';

/**
 * Funnel:
 *  * mode = Conversion -> the default is all steps are selected
 *  * mode = Frequency/TimeToConvert -> based on the first FunnelModeSlice
 */
const generateFunnelDisplayedMeasure = (
  funnel: IFunnelAnalysis,
): IDisplayedMeasureSetting[] => {
  const modeKey = funnel.query.mode?.key ?? FunnelModeKey.Conversion;
  const measureControls = funnel.chartControl.measureControls;
  switch (modeKey) {
    case FunnelModeKey.Conversion:
      // all measures are visible by default
      return measureControls.map((measure) => ({
        format: null,
        key: measure.key,
        isVisible: true,
      }));
    case FunnelModeKey.Frequency:
    case FunnelModeKey.TimeToConvert:
      // use the first funnel mode slice to determine which measures should be selected by default
      assertNotNil(funnel.query);
      const funnelModeSlices = funnel.query.modeSlices;
      const firstFunnelModeSlice =
        funnelModeSlices.length > 0 ? funnelModeSlices[0] : undefined;
      return measureControls.map((measure, index) => ({
        format: null,
        key: measure.key,
        // if no funnel mode slices -> select all measures
        isVisible: firstFunnelModeSlice?.measureSelection[index] ?? true,
      }));

    default:
      throw new UnreachableCaseError(modeKey);
  }
};

export const generateFunnelDefaultSettings = (
  analysis: IFunnelAnalysis,
  reportSetting: Maybe<IReportSettingFieldsFragment>,
): IChartSettings => {
  const defaultChartType = mapDefaultChartKindToChartType(
    analysis.chartControl.default,
  );
  // For Funnel, we select all steps and the first slice
  const displayedMeasureSettings = generateFunnelDisplayedMeasure(analysis);
  assertNotNil(analysis.data);
  const displayedSliceSettings = generateDisplayedSliceSettings({
    analysis,
    visibleCount: 1,
  });

  // if there is only one date, auto expand the funnel, but only when it is Conversion funnel
  let defaultSingleDayLabel = '';
  if (
    analysis.query.mode?.key === FunnelModeKey.Conversion &&
    analysis.data.dates.length === 1 &&
    !isTransientAnalysisId(analysis.id)
  ) {
    defaultSingleDayLabel = analysis.data.dates[0];
  }

  const isFunnelRelativeLabels = reportSetting?.funnel?.defaultConversionRate
    ? reportSetting.funnel.defaultConversionRate ===
      FunnelDefaultConversionRateSetting.Relative
    : true;

  return {
    ...initialChartSettings,
    chartType: defaultChartType,
    displayedSliceSettings,
    displayedMeasureSettings,
    singleDayLabel: defaultSingleDayLabel,
    isConversionView: true,
    isShowFunnelConversionLabels: true,
    isShowFunnelBounceLabels: false,
    isFunnelRelativeLabels,
    sort: ChartSortEnum.NONE,
    expressionSettings: null,
    tableSettings: defaultTableSettings,
  };
};
